import { useEffect, useRef, useState } from 'react';
import { AppUser, Task, Workstream } from '../../pm/types';
import { DEFAULT_WORKSTREAM_COLOR } from '../../pm/constants';
import {
  createTask,
  deleteWorkstream,
  updateWorkstream,
} from '../../pm/firebase';
import { linkify } from '../../pm/linkify';
import { formatDateRange, workstreamDateRange } from '../../pm/dueDate';
import TaskRow from './TaskRow';
import WorkstreamColorPicker from './WorkstreamColorPicker';

interface Props {
  workstream: Workstream;
  tasks: Task[];
  allTasksInWorkstream: Task[]; // unfiltered, used for delete cascade
  admins: AppUser[];
  currentUid: string;
  // Workstream drag-and-drop reorder, managed by the parent project page.
  isDragging?: boolean;
  dropIndicator?: 'before' | 'after' | null;
  onDragStartWs?: (id: string) => void;
  onDragOverWs?: (id: string, position: 'before' | 'after') => void;
  onDragLeaveWs?: (id: string) => void;
  onDropWs?: (id: string) => void;
  onDragEndWs?: () => void;
  // Task drag-and-drop (cross-workstream), also managed by the parent.
  dragKind?: 'ws' | 'task' | null;
  taskDragId?: string | null;
  taskDropTaskId?: string | null;
  taskDropPos?: 'before' | 'after' | null;
  isTaskDropTargetWs?: boolean;
  onTaskDragStart?: (id: string) => void;
  onTaskDragOverTask?: (id: string, position: 'before' | 'after') => void;
  onTaskDragLeaveTask?: (id: string) => void;
  onTaskDragOverWs?: (wsId: string) => void;
  onTaskDragLeaveWs?: (wsId: string) => void;
  onTaskDropOnTask?: (taskId: string) => void;
  onTaskDropOnWs?: (wsId: string) => void;
  onTaskDragEnd?: () => void;
  // When set, the workstream auto-expands if it contains this task so the
  // deep-link landing UX in ProjectDetail can scroll to it.
  highlightedTaskId?: string | null;
}

function collapseKey(workstreamId: string): string {
  return `pm:ws:${workstreamId}:collapsed`;
}

// Hex (#RRGGBB) → rgba(r, g, b, opacity). Tolerates leading "#" and bad input.
function tintColor(hex: string, opacity: number): string {
  const h = (hex || '').replace('#', '').trim();
  if (h.length !== 6) return `rgba(156, 163, 175, ${opacity})`; // fallback gray
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return `rgba(156, 163, 175, ${opacity})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Helper: prevent header-level click handler from firing when an interactive
// child handles the event. Wraps any handler so we can keep the children's
// existing behavior without `e.stopPropagation()` peppered everywhere.
function stop<E extends React.SyntheticEvent>(e: E) {
  e.stopPropagation();
}

const WorkstreamCard: React.FC<Props> = ({
  workstream,
  tasks,
  allTasksInWorkstream,
  admins,
  currentUid,
  isDragging = false,
  dropIndicator = null,
  onDragStartWs,
  onDragOverWs,
  onDragLeaveWs,
  onDropWs,
  onDragEndWs,
  dragKind = null,
  taskDragId = null,
  taskDropTaskId = null,
  taskDropPos = null,
  isTaskDropTargetWs = false,
  onTaskDragStart,
  onTaskDragOverTask,
  onTaskDragLeaveTask,
  onTaskDragOverWs,
  onTaskDragLeaveWs,
  onTaskDropOnTask,
  onTaskDropOnWs,
  onTaskDragEnd,
  highlightedTaskId = null,
}) => {
  const [newTask, setNewTask] = useState('');
  const [adding, setAdding] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(workstream.title);
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  // Load per-user collapsed preference from localStorage on mount.
  useEffect(() => {
    try {
      const v = window.localStorage.getItem(collapseKey(workstream.id));
      if (v === '1') setCollapsed(true);
    } catch (e) { /* ignore */ }
  }, [workstream.id]);

  // Auto-expand when a deep-linked task lands inside this workstream so the
  // shared link can actually reach the task. Doesn't touch localStorage —
  // user's stored preference takes back over on the next visit.
  useEffect(() => {
    if (!highlightedTaskId) return;
    const here = allTasksInWorkstream.some((t) => t.id === highlightedTaskId);
    if (here) setCollapsed(false);
  }, [highlightedTaskId, allTasksInWorkstream]);

  useEffect(() => {
    if (!editingTitle) setTitleDraft(workstream.title);
  }, [workstream.title, editingTitle]);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  // Display order:
  //   • open tasks first, in their stored sortOrder (drag-and-drop friendly)
  //   • done tasks below, most-recently-finished first
  // Done tasks keep their DB sortOrder untouched, so un-marking one pops it
  // back into its original slot among the open tasks.
  const sorted = [...tasks].sort((a, b) => {
    const aDone = a.status === 'done';
    const bDone = b.status === 'done';
    if (aDone !== bDone) return aDone ? 1 : -1;
    if (aDone && bDone) {
      return (b.updatedAt || 0) - (a.updatedAt || 0);
    }
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });
  const totalCount = allTasksInWorkstream.length;
  const doneCount = allTasksInWorkstream.filter((t) => t.status === 'done').length;
  const color = workstream.color || DEFAULT_WORKSTREAM_COLOR;
  const archived = !!workstream.archived;
  // Rolled-up due-date range for the workstream. Built off the full task set
  // (not the filter-trimmed list) so the chip stays meaningful when the user
  // is filtering by owner or status.
  const dateRange = workstreamDateRange(allTasksInWorkstream);

  function toggleCollapsed() {
    setCollapsed((v) => {
      const next = !v;
      try {
        if (next) window.localStorage.setItem(collapseKey(workstream.id), '1');
        else window.localStorage.removeItem(collapseKey(workstream.id));
      } catch (e) { /* ignore */ }
      return next;
    });
  }

  async function commitTitleEdit() {
    const next = titleDraft.trim();
    if (!next) {
      setTitleDraft(workstream.title);
      setEditingTitle(false);
      return;
    }
    if (next === workstream.title) {
      setEditingTitle(false);
      return;
    }
    await updateWorkstream(workstream.id, { title: next });
    setEditingTitle(false);
  }

  function cancelTitleEdit() {
    setTitleDraft(workstream.title);
    setEditingTitle(false);
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const label = newTask.trim();
    if (!label) return;
    setAdding(true);
    try {
      const nextSortOrder = totalCount;
      await createTask(
        { projectId: workstream.projectId, workstreamId: workstream.id, label, ownerId: '', sortOrder: nextSortOrder },
        currentUid,
      );
      setNewTask('');
    } finally {
      setAdding(false);
    }
  }

  async function handleDeleteWorkstream() {
    if (!window.confirm(`Delete workstream "${workstream.title}" and its ${totalCount} task(s)?`)) return;
    await deleteWorkstream(workstream.id, allTasksInWorkstream);
  }

  async function handleArchive() {
    await updateWorkstream(workstream.id, { archived: !archived });
  }

  async function handleColorChange(next: string | null) {
    await updateWorkstream(workstream.id, { color: next });
  }

  return (
    <div
      className={[
        'pm-workstream',
        archived ? 'pm-workstream-archived' : '',
        collapsed ? 'pm-workstream-collapsed' : '',
        isDragging ? 'pm-workstream-dragging' : '',
        dropIndicator === 'before' ? 'pm-workstream-drop-before' : '',
        dropIndicator === 'after' ? 'pm-workstream-drop-after' : '',
        isTaskDropTargetWs ? 'pm-workstream-task-drop-target' : '',
      ].filter(Boolean).join(' ')}
      style={{
        borderLeftColor: color,
        // Diluted color used by the header :hover rule in theme.css.
        ['--ws-color-tint' as any]: tintColor(color, 0.12),
      }}
      onDragOver={(e) => {
        // Dispatch based on which kind of drag is in flight. The workstream
        // element doubles as the drop target for both workstream reordering
        // and "drop task at end of this workstream".
        if (dragKind === 'ws') {
          if (!onDragOverWs) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const rect = e.currentTarget.getBoundingClientRect();
          const midY = rect.top + rect.height / 2;
          const position: 'before' | 'after' = e.clientY < midY ? 'before' : 'after';
          onDragOverWs(workstream.id, position);
        } else if (dragKind === 'task') {
          if (!onTaskDragOverWs) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          // Only call when no specific task is currently hovered — task
          // onDragOver stops propagation, so reaching this code means the
          // cursor is in the workstream gap area.
          onTaskDragOverWs(workstream.id);
        }
      }}
      onDragLeave={() => {
        if (dragKind === 'ws') onDragLeaveWs && onDragLeaveWs(workstream.id);
        else if (dragKind === 'task') onTaskDragLeaveWs && onTaskDragLeaveWs(workstream.id);
      }}
      onDrop={(e) => {
        if (dragKind === 'ws') {
          if (!onDropWs) return;
          e.preventDefault();
          onDropWs(workstream.id);
        } else if (dragKind === 'task') {
          if (!onTaskDropOnWs) return;
          e.preventDefault();
          onTaskDropOnWs(workstream.id);
        }
      }}
    >
      <div
        className="pm-workstream-header"
        draggable={!!onDragStartWs}
        onDragStart={(e) => {
          if (!onDragStartWs) return;
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', workstream.id);
          onDragStartWs(workstream.id);
        }}
        onDragEnd={() => onDragEndWs && onDragEndWs()}
        onClick={toggleCollapsed}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          // Treat Enter/Space on the header itself as a collapse toggle, but
          // ignore keydowns that bubbled from a focused child element.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleCollapsed();
          }
        }}
        aria-expanded={!collapsed}
      >
        <div className="pm-workstream-header-main">
          {/* Drag-handle affordance; the entire header is the draggable area. */}
          <span className="pm-ws-drag-grip" aria-hidden="true">
            <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
              <circle cx="2" cy="2" r="1.2" />
              <circle cx="8" cy="2" r="1.2" />
              <circle cx="2" cy="7" r="1.2" />
              <circle cx="8" cy="7" r="1.2" />
              <circle cx="2" cy="12" r="1.2" />
              <circle cx="8" cy="12" r="1.2" />
            </svg>
          </span>
          {/* Color picker — clicks shouldn't bubble to header collapse */}
          <span onClick={stop}>
            <WorkstreamColorPicker color={workstream.color} onChange={handleColorChange} />
          </span>
          {editingTitle ? (
            <input
              ref={titleInputRef}
              className="pm-ws-title-edit-input"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitleEdit}
              onClick={stop}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitTitleEdit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelTitleEdit();
                }
              }}
            />
          ) : (
            <h2
              className="pm-ws-title"
              onClick={(e) => {
                e.stopPropagation();
                setEditingTitle(true);
              }}
              title="Click to edit workstream title"
            >
              {linkify(workstream.title, { stopPropagation: true })}
            </h2>
          )}
        </div>
        <div className="pm-workstream-header-meta">
          {dateRange ? (
            <span
              className="pm-ws-date-range"
              title="Earliest – latest due date across this workstream's tasks"
            >
              {formatDateRange(dateRange.min, dateRange.max)}
            </span>
          ) : null}
          <span className="pm-workstream-count">{doneCount}/{totalCount} done</span>
          <button
            type="button"
            className="pm-link-button"
            onClick={(e) => { stop(e); handleArchive(); }}
            title={archived ? 'Restore this workstream' : 'Archive this workstream'}
          >
            {archived ? 'Unarchive' : 'Archive'}
          </button>
          <button
            type="button"
            className="pm-link-button pm-danger"
            onClick={(e) => { stop(e); handleDeleteWorkstream(); }}
          >
            Delete
          </button>
          <button
            type="button"
            className="pm-ws-collapse-toggle"
            onClick={(e) => { stop(e); toggleCollapsed(); }}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand workstream' : 'Collapse workstream'}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 120ms' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>
      {!collapsed ? (
        <>
          {sorted.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              admins={admins}
              currentUid={currentUid}
              isDragging={taskDragId === t.id}
              dropIndicator={taskDropTaskId === t.id ? taskDropPos : null}
              onDragStartTask={onTaskDragStart}
              onDragOverTask={onTaskDragOverTask}
              onDragLeaveTask={onTaskDragLeaveTask}
              onDropTask={onTaskDropOnTask}
              onDragEndTask={onTaskDragEnd}
              highlighted={highlightedTaskId === t.id}
            />
          ))}
          <form className="pm-inline-add" onSubmit={handleAdd}>
            <input
              placeholder="Add a task and press Enter"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
            />
            <button className="pm-button" type="submit" disabled={adding || !newTask.trim()}>
              {adding ? 'Adding…' : 'Add'}
            </button>
          </form>
        </>
      ) : null}
    </div>
  );
};

export default WorkstreamCard;
